import { grpcClientFactory } from '../../grpcClientFactory';
import { pendingDownloadsStorage } from './pendingDownloads';
import { eventBus } from '../../../events';
// A download that keeps erroring must not hammer the server every 3s forever.
// Back off on consecutive failures, reset to the base interval on any reply.
const POLL_BACKOFF_CEILING_MS = 60000;
class DownloadService {
    constructor() {
        this.listenersInitialized = false;
        // One poll chain per download id. Presence in the map means "polling";
        // the value is the pending timer, or null while a request is in flight.
        // Without this, resumePendingDownloads and a fresh create both start their
        // own self-perpetuating timer for the same id and nothing can stop either.
        this.activePolls = new Map();
    }
    createAuthenticatedClient() {
        return grpcClientFactory.createAuthenticatedUserProjectsClient({ enableCompression: true });
    }
    stopPolling(downloadId) {
        const timer = this.activePolls.get(downloadId);
        if (timer)
            clearTimeout(timer);
        this.activePolls.delete(downloadId);
    }
    schedulePoll(downloadId, tick, delayMs) {
        // Dropped from the map means someone stopped this chain while the request
        // was in flight — don't resurrect it.
        if (!this.activePolls.has(downloadId))
            return;
        this.activePolls.set(downloadId, setTimeout(tick, delayMs));
    }
    initListeners() {
        if (this.listenersInitialized)
            return;
        this.listenersInitialized = true;
        // Listen for download creation requests via event bus
        eventBus.on('download:create', ({ projectId }) => {
            if (projectId) {
                this.createProjectDownload(projectId).catch(() => { });
            }
        });
        // Listen for download consumption (removal from pending)
        eventBus.on('download:consume', ({ downloadId }) => {
            if (downloadId) {
                pendingDownloadsStorage.remove(downloadId);
                this.stopPolling(downloadId);
            }
        });
        // Handle demo project downloads
        eventBus.on('download:demoProjectCreated', ({ projectId, downloadId }) => {
            if (projectId && downloadId) {
                pendingDownloadsStorage.add({ projectId, downloadId, startedAt: Date.now(), serviceType: 'demo' });
                this.pollDownloadStatus(downloadId, 3000);
            }
        });
        this.resumePendingDownloads();
    }
    async createProjectDownload(projectId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken)
            throw new Error('auth required');
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.CreateProjectDownloadRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.createProjectDownload(grpcReq, metadata, (err, resp) => {
                    var _a;
                    if (err || !resp) {
                        reject(err || new Error('no response'));
                        return;
                    }
                    const downloadId = (_a = resp.getDownloadId) === null || _a === void 0 ? void 0 : _a.call(resp);
                    if (!downloadId) {
                        reject(new Error('no download id'));
                        return;
                    }
                    pendingDownloadsStorage.add({ projectId, downloadId, startedAt: Date.now(), serviceType: 'user' });
                    resolve(downloadId);
                    this.pollDownloadStatus(downloadId, 3000);
                });
            }
            catch (e) {
                reject(e);
            }
        });
    }
    pollDownloadStatus(downloadId, intervalMs) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken)
            return;
        if (this.activePolls.has(downloadId))
            return;
        this.activePolls.set(downloadId, null);
        let failures = 0;
        const resolveProjectId = () => {
            var _a;
            return (_a = pendingDownloadsStorage.findByDownloadId(downloadId)) === null || _a === void 0 ? void 0 : _a.projectId;
        };
        const getServiceType = () => {
            var _a;
            return ((_a = pendingDownloadsStorage.findByDownloadId(downloadId)) === null || _a === void 0 ? void 0 : _a.serviceType) || 'user';
        };
        // Reply received: clear the backoff and go again at the base cadence.
        const scheduleNext = () => {
            failures = 0;
            this.schedulePoll(downloadId, tick, intervalMs);
        };
        const scheduleRetry = () => {
            failures += 1;
            const delay = Math.min(intervalMs * 2 ** (failures - 1), POLL_BACKOFF_CEILING_MS);
            this.schedulePoll(downloadId, tick, delay);
        };
        const emitStatus = (status, url, error, expiresAt) => {
            const payload = {
                status: status,
                downloadId,
                projectId: resolveProjectId(),
                url,
                error,
                expiresAt
            };
            eventBus.emit('download:status', payload);
        };
        const Req = proto.landscope.mapviewer.v1.GetProjectDownloadStatusRequest;
        const req = new Req();
        req.setDownloadId(downloadId);
        const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
        const client = this.createAuthenticatedClient();
        // Terminal outcomes end the chain; anything else keeps it going.
        const applyStatus = (status, url, error, expiresAt) => {
            emitStatus(status, url, error, expiresAt);
            if (status === 'ready') {
                pendingDownloadsStorage.update(downloadId, { status: 'ready', url, expiresAt });
                this.stopPolling(downloadId);
                return;
            }
            if (status === 'failed' || status === 'expired') {
                pendingDownloadsStorage.remove(downloadId);
                this.stopPolling(downloadId);
                return;
            }
            scheduleNext();
        };
        const tick = () => {
            // The download was consumed or cleared out from under us — nothing left
            // to poll for, so let the chain die instead of running for the life of
            // the page.
            if (!pendingDownloadsStorage.findByDownloadId(downloadId)) {
                this.stopPolling(downloadId);
                return;
            }
            const serviceType = getServiceType();
            const isDemo = serviceType === 'demo';
            if (isDemo) {
                import('../../demoProjectsService')
                    .then(({ demoProjectsService }) => demoProjectsService.getProjectDownloadStatus(downloadId))
                    .then((resp) => {
                    applyStatus(resp.status, resp.presignedUrl, resp.error, resp.expiresAt);
                })
                    .catch(() => {
                    scheduleRetry();
                });
            }
            else {
                client.getProjectDownloadStatus(req, metadata, (err, resp) => {
                    var _a, _b, _c;
                    if (err || !resp) {
                        scheduleRetry();
                        return;
                    }
                    const status = (_a = resp.getStatus) === null || _a === void 0 ? void 0 : _a.call(resp);
                    const url = (_b = resp.getPresignedUrl) === null || _b === void 0 ? void 0 : _b.call(resp);
                    const error = (_c = resp.getError) === null || _c === void 0 ? void 0 : _c.call(resp);
                    const expiresAt = typeof resp.getExpiresAt === 'function' ? resp.getExpiresAt() : undefined;
                    applyStatus(status, url, error, expiresAt);
                });
            }
        };
        tick();
    }
    resumePendingDownloads() {
        const now = Date.now();
        const list = pendingDownloadsStorage.read();
        if (!list.length)
            return;
        const filtered = list.filter(item => {
            if (item.status === 'ready' && item.expiresAt && now > item.expiresAt) {
                return false;
            }
            return true;
        });
        if (filtered.length !== list.length) {
            pendingDownloadsStorage.write(filtered);
        }
        filtered.forEach(item => {
            if (item.status === 'ready' && item.url) {
                const payload = {
                    status: 'ready',
                    downloadId: item.downloadId,
                    projectId: item.projectId,
                    url: item.url,
                    expiresAt: item.expiresAt
                };
                eventBus.emit('download:status', payload);
            }
            else {
                this.pollDownloadStatus(item.downloadId, 3000);
            }
        });
    }
    getReadyDownloadForProject(projectId) {
        return pendingDownloadsStorage.findReadyByProjectId(projectId);
    }
}
export const downloadService = new DownloadService();
